/**
 * In-flight ticker bar — Stage 3 deliverable.
 *
 * A slim chrome strip that lives between the Topbar and the page body
 * showing orders/min, GMV/min and exceptions/min derived from the global
 * `/ws/orders/` firehose. Hidden on the auth screens via the AppShell
 * compositional ordering.
 *
 * The bar is intentionally subtle — a 28px tall row with bullet
 * separators — because Live Ops ops staff already have a dedicated page;
 * this is the ambient signal for everyone else.
 */
import { Activity, AlertTriangle, Banknote } from 'lucide-react';
import { Tooltip } from '@/components/ui/Tooltip';
import { useLiveTicker } from '@/features/live/useLiveTicker';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

export function LiveTickerBar() {
  const t = useLiveTicker();
  const dot =
    t.status === 'open'
      ? 'live-dot'
      : t.status === 'connecting'
        ? 'live-dot live-dot-warning'
        : 'live-dot live-dot-danger';

  return (
    <div
      role="status"
      aria-label="Live operations ticker"
      className="hidden items-center gap-3 border-b border-border bg-surface/60 px-4 py-1 text-[11px] text-muted-foreground backdrop-blur supports-[backdrop-filter]:bg-surface/40 lg:flex"
    >
      <span className="inline-flex items-center gap-1.5">
        <span className={cn(dot)} aria-hidden />
        <span className="uppercase tracking-wider">live</span>
      </span>

      <Sep />

      <Tooltip content="Orders created in the last 5 minutes (per minute)">
        <span className="inline-flex items-center gap-1">
          <Activity className="h-3 w-3" aria-hidden />
          <span className="kpi-num font-medium text-foreground">{fmt.number(t.ordersPerMin)}</span>
          <span>orders/min</span>
        </span>
      </Tooltip>

      <Sep />

      <Tooltip content="Revenue captured in the last 5 minutes (per minute)">
        <span className="inline-flex items-center gap-1">
          <Banknote className="h-3 w-3" aria-hidden />
          <span className="kpi-num font-medium text-foreground">{fmt.npr(t.gmvPerMin)}</span>
          <span>/min</span>
        </span>
      </Tooltip>

      <Sep />

      <Tooltip content="Cancelled or late-tagged orders in the rolling 5-minute window">
        <span className={cn('inline-flex items-center gap-1', t.exceptionsPerMin > 0 && 'text-warning-soft')}>
          <AlertTriangle className="h-3 w-3" aria-hidden />
          <span className="kpi-num font-medium">{fmt.number(t.exceptionsPerMin)}</span>
          <span>exceptions/min</span>
        </span>
      </Tooltip>

      <Sep />

      <span className="ml-auto text-[10px] uppercase tracking-wider opacity-70">
        {t.lastEventAt ? `last event ${fmt.ago(new Date(t.lastEventAt))}` : 'awaiting first event…'}
      </span>
    </div>
  );
}

function Sep() {
  return <span aria-hidden className="text-muted-foreground/40">·</span>;
}
